import React from 'react';
import _ from 'underscore';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ContainerSummary, GridCellSummary, StorableSummary} from 'constants/SampleConstants';
import ContainerLink from 'samples/containers/ContainerLink';
import * as StorableActions from 'samples/StorableActions';
import {inputSearchStorables, STORABLES_INPUT_SEARCH_ENDPOINT} from 'samples/StorableDAO';
import AncestralStorables from 'samples/storables/AncestralStorables';
import StorableStore from 'samples/StorableStore';
import {Filter, FilterKey, Operator, SortKey} from 'search/SearchConstants';
import {DEFAULT_DROPDOWN_SEARCH_LIMIT} from 'ui-library/dropdowns/SearchDropdownConstants';
import * as RichObjectTable from 'ui-library/RichObjectTable';
import SearchCell from 'ui-library/SearchCell';
import {StageRunSample} from 'workflows/WorkflowConstants';

import ActionCell from './ActionCell';
import OptionalContainerLink from './OptionalContainerLink';

export interface SelectContainerFromContainableCellProps {
  sample: StageRunSample;
  isSaving: boolean;
  optimisticContainerId: string | null;
  onSelectContainer: (container: ContainerSummary | null) => Promise<void>;
}

interface State {
  queryText: string;
  isSearching: boolean;
}

interface SelectedContainer {
  name: string;
  item_id: string;
  item: StorableSummary;
  type: string;
  extra: {
    ancestralStorables: Array<StorableSummary>;
    gridCell: GridCellSummary | null;
  };
}

/**
 * Component for selecting either an available container for the given sample or no container.
 */
class SelectContainerFromContainableCell extends React.Component<
  SelectContainerFromContainableCellProps,
  State
> {
  override state: State = {
    queryText: '',
    isSearching: false,
  };

  override componentDidUpdate(prevProps: SelectContainerFromContainableCellProps): void {
    if (
      this.props.sample.containerId &&
      prevProps.sample.containerId !== this.props.sample.containerId &&
      !StorableStore.getContainer(this.props.sample.containerId)
    ) {
      StorableActions.fetchContainer(this.props.sample.containerId, true);
    }
  }

  _createSearchPromise = async (q: string): Promise<Array<SelectedContainer> | undefined> => {
    const typesFilter: Filter = {
      key: FilterKey.TYPES,
      operator: Operator.IS_ONE_OF,
      value: [ModelTypeConstants.CONTAINER],
    };
    const {batch, fileId} = this.props.sample;
    const containableId = batch ? batch.id : fileId;

    // @ts-expect-error TODO(BNCH-56919): Fix this. `containableId` is of type string, but
    // `ContainerContentsFilter.value` expects an object with specific fields to be defined.
    const containableFilter: Filter | undefined =
      containableId != null
        ? {key: FilterKey.CONTAINER_CONTENTS, operator: Operator.EQ, value: containableId}
        : undefined;
    const filters = _.compact([typesFilter, containableFilter]);

    const response = await inputSearchStorables({
      query: q,
      filters,
      offset: 0,
      sorts: [{key: SortKey.SORT_RELEVANCE_KEY, reverse: false}],
      limit: DEFAULT_DROPDOWN_SEARCH_LIMIT,
    });
    return response.storables.map((container: StorableSummary) => ({
      name: container.name,
      item_id: container.id,
      item: container,
      type: ModelTypeConstants.CONTAINER,
      extra: {
        ancestralStorables: response.storableSummaryExtras.ancestralStorablesById![container.id],
        gridCell: response.storableSummaryExtras.gridCellsByContainerId![container.id],
      },
    }));
  };

  override render(): JSX.Element {
    if (this.props.isSaving) {
      return (
        <RichObjectTable.Cell primary={true}>
          <RichObjectTable.SpinningCell>
            {this.props.sample.containerId ? (
              <OptionalContainerLink containerId={this.props.sample.containerId} />
            ) : (
              <OptionalContainerLink />
            )}
          </RichObjectTable.SpinningCell>
        </RichObjectTable.Cell>
      );
    }
    if (!this.props.sample.containerId && !this.state.isSearching) {
      return (
        <RichObjectTable.Cell>
          <div className="u-vertical-center-items">
            <div
              className="u-flex-grow u-word-break-word"
              onClick={() => {
                this.setState({isSearching: true});
              }}
            >
              <span className="u-faded">Search container...</span>
            </div>
          </div>
        </RichObjectTable.Cell>
      );
    } else if (!this.props.sample.containerId) {
      return (
        <RichObjectTable.Cell>
          <SearchCell
            allowEmptyQuery={true}
            onCancel={() => {
              this.setState({isSearching: false, queryText: ''});
            }}
            onCreateSearchPromise={this._createSearchPromise}
            searchEndpoint={STORABLES_INPUT_SEARCH_ENDPOINT}
            onSelectItem={this.props.onSelectContainer}
            onSetQueryText={(queryText: string) => {
              this.setState({queryText});
            }}
            queryText={this.state.queryText}
          />
        </RichObjectTable.Cell>
      );
    } else {
      return (
        <RichObjectTable.Cell>
          <ActionCell icon="delete" onClick={() => this.props.onSelectContainer(null)}>
            <ContainerLink containerId={this.props.sample.containerId} />
            <br />
            <AncestralStorables
              className="richObjectTable-detailText"
              noLocationText="No location"
              storableId={this.props.sample.containerId}
              showGridCell={true}
              truncate={true}
            />
          </ActionCell>
        </RichObjectTable.Cell>
      );
    }
  }
}

export default SelectContainerFromContainableCell;
